import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import api from "../../utils/api";
import EditCouponModal from "./editCouponModal";
import "react-toastify/dist/ReactToastify.css";

const PRIMARY = "#f66061";

// ── Shared atom styles ──────────────────────────────────────────────────────
const inputBase =
  "w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 " +
  "focus:outline-none focus:ring-2 focus:ring-[#f66061]/40 focus:border-[#f66061] transition-all duration-150";

const btnBase =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all duration-150 whitespace-nowrap";

const Btn = {
  primary: `${btnBase} text-white hover:opacity-90 active:scale-95`,
  ghost:   `${btnBase} bg-white border border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900 active:scale-95`,
  danger:  `${btnBase} bg-white border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400 active:scale-95`,
  success: `${btnBase} bg-white border border-green-200 text-green-600 hover:bg-green-50 hover:border-green-400 active:scale-95`,
  muted:   `${btnBase} bg-gray-100 text-gray-500 hover:bg-gray-200 active:scale-95`,
};

// ── Status badge ────────────────────────────────────────────────────────────
const StatusBadge = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
      Inactive
    </span>
  );

// ── Confirm dialog ──────────────────────────────────────────────────────────
const ConfirmDialog = ({ coupon, onConfirm, onCancel }) => {
  const deactivating = coupon.isActive;
  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
        {/* Icon */}
        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-xl ${deactivating ? "bg-red-50" : "bg-emerald-50"}`}>
          {deactivating ? "⚠️" : "✅"}
        </div>

        {/* Copy */}
        <div>
          <p className="font-semibold text-gray-900 text-base">
            {deactivating ? "Deactivate coupon?" : "Activate coupon?"}
          </p>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
            {deactivating ? (
              <>Coupon <span className="font-mono font-bold text-gray-800">{coupon.code}</span> will no longer be usable by customers.</>
            ) : (
              <>Coupon <span className="font-mono font-bold text-gray-800">{coupon.code}</span> will become available for customers to use.</>
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button onClick={onCancel} className={`${Btn.muted} flex-1`}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`${btnBase} flex-1 text-white`}
            style={{ backgroundColor: deactivating ? "#ef4444" : "#10b981" }}
          >
            {deactivating ? "Yes, deactivate" : "Yes, activate"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Mobile card ─────────────────────────────────────────────────────────────
const CouponCard = ({ c, onToggleRequest, onEdit }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3 shadow-sm">
    <div className="flex items-start justify-between gap-2">
      <span className="font-mono font-bold text-base text-gray-900 tracking-widest">{c.code}</span>
      <StatusBadge active={c.isActive} />
    </div>
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div className="bg-gray-50 rounded-lg px-3 py-2">
        <p className="text-xs text-gray-400 mb-0.5">Discount</p>
        <p className="font-semibold text-gray-800">{c.discountPercentage}%</p>
      </div>
      <div className="bg-gray-50 rounded-lg px-3 py-2">
        <p className="text-xs text-gray-400 mb-0.5">Applicable Cards</p>
        <p className="font-semibold text-gray-800">{c.applicableCards}</p>
      </div>
    </div>
    <div className="flex gap-2 pt-1">
      <button
        onClick={() => onToggleRequest(c._id)}
        className={c.isActive ? Btn.danger : Btn.success}
        style={{ flex: 1 }}
      >
        {c.isActive ? "Deactivate" : "Activate"}
      </button>
      <button
        onClick={() => onEdit(c)}
        className={Btn.primary}
        style={{ flex: 1, backgroundColor: PRIMARY }}
      >
        Edit
      </button>
    </div>
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────
const CouponsListing = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editCoupon, setEditCoupon] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // coupon awaiting confirm

  const [searchCode, setSearchCode] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchCoupons = useCallback(
    async ({ pageNumber = 1, code = "", status = "all" } = {}) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: pageNumber, limit: 10 });
        if (code) params.append("code", code);
        if (status !== "all") params.append("isActive", status === "active");

        const res = await api.get(`/coupon/list?${params.toString()}`);
        if (res.data.code === 200) {
          setCoupons(res.data.data);
          setPages(res.data.pagination.pages);
          setPage(res.data.pagination.page);
          setTotal(res.data.pagination.total);
        }
      } catch {
        toast.error("Failed to fetch coupons");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchCoupons({ pageNumber: 1, code: "", status: "all" });
  }, [fetchCoupons]);

  const goToPage = (next) => {
    setPage(next);
    fetchCoupons({ pageNumber: next, code: searchCode, status: statusFilter });
  };

  const handleSearch = () =>
    fetchCoupons({ pageNumber: 1, code: searchCode, status: statusFilter });

  const handleReset = () => {
    setSearchCode("");
    setStatusFilter("all");
    fetchCoupons({ pageNumber: 1, code: "", status: "all" });
  };

  // Step 1: open confirm dialog
  const handleToggleRequest = (id) => {
    const coupon = coupons.find((c) => c._id === id);
    setConfirmTarget(coupon);
  };

  // Step 2: user confirmed → hit API
  const handleToggleConfirm = async () => {
    const coupon = confirmTarget;
    setConfirmTarget(null);
    try {
      const res = await api.put(`/coupon/edit/${coupon._id}`, {
        isActive: !coupon.isActive,
      });
      if (res.data.code === 200) {
        setCoupons((prev) =>
          prev.map((c) => (c._id === coupon._id ? res.data.data : c))
        );
        toast.success(
          `Coupon ${res.data.data.isActive ? "activated" : "deactivated"}`
        );
      }
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-6">
      <ToastContainer position="top-center" autoClose={4000} hideProgressBar={false} closeOnClick draggable pauseOnHover />

      {/* ── Header ── */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Coupons</h2>
        <p className="text-sm text-gray-400 mt-0.5">{total} coupon{total !== 1 ? "s" : ""} total</p>
      </div>

      {/* ── Filter bar ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Code</label>
            <input
              type="text"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Search by coupon code…"
              className={inputBase}
            />
          </div>
          <div className="sm:w-44">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputBase}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={handleSearch} className={Btn.primary} style={{ backgroundColor: PRIMARY, minWidth: "88px" }}>
              Search
            </button>
            <button onClick={handleReset} className={Btn.muted} style={{ minWidth: "72px" }}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <ClipLoader size={36} color={PRIMARY} />
        </div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm">
          <p className="text-gray-300 text-5xl mb-3">🏷️</p>
          <p className="text-gray-500 font-medium">No coupons found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="flex flex-col gap-3 sm:hidden">
            {coupons.map((c) => (
              <CouponCard key={c._id} c={c} onToggleRequest={handleToggleRequest} onEdit={setEditCoupon} />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {["Code", "Discount", "Applicable Cards", "Status"].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                  <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coupons.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50/50 transition-colors duration-100">
                    <td className="px-5 py-4 font-mono font-bold text-gray-900 tracking-widest">{c.code}</td>
                    <td className="px-5 py-4 text-gray-700 font-medium">{c.discountPercentage}%</td>
                    <td className="px-5 py-4 text-gray-700">{c.applicableCards}</td>
                    <td className="px-5 py-4"><StatusBadge active={c.isActive} /></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleRequest(c._id)}
                          className={c.isActive ? Btn.danger : Btn.success}
                        >
                          {c.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          onClick={() => setEditCoupon(c)}
                          className={Btn.primary}
                          style={{ backgroundColor: PRIMARY }}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-400 order-2 sm:order-1">
              Showing <span className="font-medium text-gray-600">{coupons.length}</span> of{" "}
              <span className="font-medium text-gray-600">{total}</span> coupons
            </p>
            <div className="flex items-center gap-1.5 order-1 sm:order-2">
              <button onClick={() => goToPage(page - 1)} disabled={page === 1} className={Btn.ghost} style={{ minWidth: "80px" }}>
                ← Prev
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      className={`${btnBase} w-9 h-9 ${p === page ? "text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-400"}`}
                      style={p === page ? { backgroundColor: PRIMARY } : {}}
                    >
                      {p}
                    </button>
                  )
                )}
              <button onClick={() => goToPage(page + 1)} disabled={page === pages} className={Btn.ghost} style={{ minWidth: "80px" }}>
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Confirm dialog ── */}
      {confirmTarget && (
        <ConfirmDialog
          coupon={confirmTarget}
          onConfirm={handleToggleConfirm}
          onCancel={() => setConfirmTarget(null)}
        />
      )}

      {/* ── Edit modal ── */}
      {editCoupon && (
        <EditCouponModal
          coupon={editCoupon}
          onClose={() => setEditCoupon(null)}
          onUpdated={(updated) => {
            setCoupons((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
            setEditCoupon(null);
          }}
        />
      )}
    </div>
  );
};

export default CouponsListing;